<script setup lang="ts">
import { useField } from "vee-validate"
import { computed, toRef } from "vue"
import { Hint } from "@/components/ui"

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<TextareaProps>(), {
  modelValue: "",
  label: "",
  id: "",
  error: "",
  hint: "",
  disabled: false,
  variant: "floating",
  placeholder: "",
})

const emit = defineEmits(["update:modelValue"])

interface TextareaProps {
  modelValue?: string
  name: string
  label?: string
  id?: string
  error?: string
  hint?: string
  disabled?: boolean
  variant?: "standard" | "floating"
  placeholder?: string
}

const name = toRef(props, "name")

const { value, errorMessage, handleBlur, handleChange } = useField(name)

const isLabelFloating = computed(() => value.value || props.modelValue || (props.placeholder && !(value.value || props.modelValue)))

function onInput(event: Event) {
  handleChange(event)
  emit("update:modelValue", (event.target as HTMLTextAreaElement).value)
}
</script>

<template>
  <div>
    <label v-if="variant === 'standard' && label" :for="id || name" class="block text-sm font-semibold text-gray-700 mb-1.5">
      <span class="flex flex-row gap-1 items-center">
        <span>{{ label }}</span>
      </span>
    </label>
    <div
      class="gpx-input-container"
      :class="{
        'gpx-input-container_error': errorMessage || error,
      }"
    >
      <label class="gpx-input-label-container" :for="id || name">
        <textarea
          :id="id || name"
          :name="name"
          v-bind="$attrs"
          :placeholder="placeholder"
          class="gpx-input"
          :class="{
            'gpx-textarea_error': errorMessage || error,
            'gpx-input_disabled': disabled,
          }"
          :disabled="disabled"
          :value="value as string ?? modelValue"
          @input="onInput"
          @blur="handleBlur"
        />
        <span
          v-if="variant === 'floating'"
          class="gpx-floating-label"
          :class="{
            'gpx-floating-label_is-floating': isLabelFloating,
            'gpx-floating-label_error': errorMessage || error,
          }"
        >{{ label }}</span>
      </label>
    </div>
    <span v-if="errorMessage || error" class="gpx-field-error" v-text="error || errorMessage" />
    <Hint v-if="hint" :hint="hint" />
  </div>
</template>

<style lang="postcss" scoped>
@reference "../../../assets/styles.css";

.gpx-input-container {
  height: auto;
  overflow: hidden;
}

.gpx-input-label-container {
  position: relative;
}

.gpx-input-label-container::before {
  @apply bg-white absolute top-0 inset-x-0;

  content: "";
  height: 2rem;
}

.gpx-floating-label {
  top: 1.5rem;
  transition:
    transform 0.2s,
    font-size 0.2s;
}

.gpx-input {
  min-height: 8rem;
  resize: vertical;
  padding-top: 2rem;
}

.gpx-input_disabled {
  resize: none;
  overflow: hidden;
}

.gpx-input:focus + .gpx-floating-label,
.gpx-floating-label_is-floating {
  @apply bg-white w-full text-xs;

  transform: translateY(-95%);
  transition:
    transform 0.2s,
    font-size 0.2s;
}

.gpx-input_disabled + .gpx-floating-label_is-floating,
.gpx-input-container:has(.gpx-input_disabled) .gpx-input-label-container::before {
  @apply bg-gray-200;
}
</style>
